import { privacyPolicy } from './privacy-policy';
import { courseUseContent, WebsiteContentBlock } from './website-content';

// Policy copy supplied in Refund.docx; course-use copy supplied in WebsiteContent1.docx.
export const policies = {
    privacy: privacyPolicy,
    courseUse: {
        title: courseUseContent.title,
        version: '2',
        blocks: courseUseContent.blocks,
    },
    terms: {
        "title": "Terms & conditions",
        "blocks": [
            {
                "type": "paragraph",
                "text": "Last updated: 10 October 2026"
            },
            {
                "type": "paragraph",
                "text": "These Terms & Conditions govern your access to and use of digital self-study courses provided by Olha Popivnenko. The courses and course materials are created and provided by Olha Popivnenko. Payments are processed by Paddle, which acts as the Merchant of Record and authorised reseller for purchases made through this website. By accepting these Terms & Conditions, you agree to the conditions set out below."
            },
            {
                "type": "heading",
                "text": "1. Course access"
            },
            {
                "type": "paragraph",
                "text": "Each purchased course includes lifetime access, unless a different access period is clearly stated on the relevant course page. During your access period, you may return to the course through your personal account and continue your progress. Where supported by the platform, completed activities, scores, saved answers and other progress data will remain linked to your account."
            },
            {
                "type": "heading",
                "text": "2. Access after the 12-month period"
            },
            {
                "type": "paragraph",
                "text": "If your access expires before you have completed the course, you may contact us by email to request restoration of access. Where our platform records reasonably confirm that you did not complete the course during the original access period, access may be restored without additional charge so that you can complete the course. Restoration of access is intended to allow you to complete the course originally purchased. It does not constitute a new purchase and does not create a right to a refund of the original purchase price."
            },
            {
                "type": "heading",
                "text": "3. Your account"
            },
            {
                "type": "paragraph",
                "text": "Course access is linked to your personal account. You are responsible for keeping your login details secure."
            },
            {
                "type": "paragraph",
                "text": "You must not:"
            },
            {
                "type": "list",
                "items": [
                    "share your account with another person;",
                    "allow another person to access a purchased course through your account;",
                    "sell or transfer your account;",
                    "disclose your login details for the purpose of giving another person access to course content."
                ]
            },
            {
                "type": "paragraph",
                "text": "If you believe that your account has been accessed without your permission, please contact us as soon as possible."
            },
            {
                "type": "heading",
                "text": "4. Licence to use the course"
            },
            {
                "type": "paragraph",
                "text": "Purchasing a course gives you a personal, limited, non-exclusive and non-transferable licence to access and use that course for your own learning and professional development during the applicable access period. Purchasing access does not transfer ownership of the course or its intellectual property to you. You are free to apply the knowledge, skills, teaching techniques and professional insights gained through the course in your own teaching practice. Detailed rules concerning copying, sharing, reproducing and otherwise using course materials are contained in the separate Course Use & Intellectual Property Conditions, which you will be asked to acknowledge before entering a purchased course for the first time."
            },
            {
                "type": "heading",
                "text": "5. Intellectual property"
            },
            {
                "type": "paragraph",
                "text": "Unless expressly stated otherwise, original materials provided through Triad Teacher Training remain the intellectual property of Olha Popivnenko."
            },
            {
                "type": "paragraph",
                "text": "This includes, where applicable:"
            },
            {
                "type": "list",
                "items": [
                    "written course content;",
                    "explanations;",
                    "activities and exercises;",
                    "assessments and quizzes;",
                    "course and lesson structure;",
                    "original frameworks;",
                    "model answers;",
                    "graphics and images;",
                    "downloadable resources;",
                    "other original educational materials."
                ]
            },
            {
                "type": "paragraph",
                "text": "Nothing in these Terms transfers ownership of this intellectual property to the user."
            },
            {
                "type": "heading",
                "text": "6. Prohibited use of the platform"
            },
            {
                "type": "paragraph",
                "text": "You may not:"
            },
            {
                "type": "list",
                "items": [
                    "attempt to gain unauthorised access to another user's account;",
                    "bypass or attempt to bypass course access restrictions;",
                    "interfere with the security or normal operation of the website;",
                    "use automated tools to systematically scrape, copy or extract course content;",
                    "upload malicious software, code or other harmful material;",
                    "use the website for fraudulent or unlawful purposes;",
                    "use the platform in a way that deliberately disrupts access for other users."
                ]
            },
            {
                "type": "heading",
                "text": "7. Suspension or termination of access"
            },
            {
                "type": "paragraph",
                "text": "We may suspend or terminate access where we reasonably believe that there has been:"
            },
            {
                "type": "list",
                "items": [
                    "account sharing;",
                    "unauthorised distribution of course materials;",
                    "serious or repeated infringement of intellectual property rights;",
                    "fraudulent use of the platform;",
                    "attempted unauthorised access;",
                    "deliberate interference with website security;",
                    "another serious breach of these Terms & Conditions."
                ]
            },
            {
                "type": "paragraph",
                "text": "Where reasonably appropriate, you will be given an opportunity to explain or resolve the issue before permanent access is removed. Any action taken will be proportionate to the circumstances. Nothing in this section limits any consumer right that cannot legally be excluded."
            },
            {
                "type": "heading",
                "text": "8. Course updates"
            },
            {
                "type": "paragraph",
                "text": "Course content may be updated from time to time in order to:"
            },
            {
                "type": "list",
                "items": [
                    "correct errors;",
                    "improve explanations or activities;",
                    "improve course design or usability;",
                    "update professional information;",
                    "reflect relevant developments in teaching practice;",
                    "improve technical functionality."
                ]
            },
            {
                "type": "paragraph",
                "text": "Updates will not intentionally remove the core subject matter or materially reduce the overall value of a course already purchased."
            },
            {
                "type": "heading",
                "text": "9. Website and course availability"
            },
            {
                "type": "paragraph",
                "text": "We aim to keep the website and purchased courses available reliably. However, uninterrupted access cannot be guaranteed at all times."
            },
            {
                "type": "paragraph",
                "text": "Temporary interruptions may occur because of:"
            },
            {
                "type": "list",
                "items": [
                    "scheduled or emergency maintenance;",
                    "software updates;",
                    "hosting or infrastructure problems;",
                    "security incidents;",
                    "third-party service failures;",
                    "circumstances outside our reasonable control."
                ]
            },
            {
                "type": "paragraph",
                "text": "Where reasonably possible, access will be restored as soon as practicable."
            },
            {
                "type": "heading",
                "text": "10. Technical requirements"
            },
            {
                "type": "paragraph",
                "text": "You are responsible for having a suitable device, internet connection and modern web browser capable of accessing the platform. If a particular course requires additional software or technical capabilities, this will be stated in the relevant course description where appropriate. If you experience a technical issue that appears to originate from the Triad Teacher Training platform, please contact us."
            },
            {
                "type": "heading",
                "text": "11. Educational and professional outcomes"
            },
            {
                "type": "paragraph",
                "text": "Triad Teacher Training courses are designed to support learning and professional development."
            },
            {
                "type": "paragraph",
                "text": "Purchasing or completing a course does not guarantee:"
            },
            {
                "type": "list",
                "items": [
                    "admission to CELTA, DELTA or another qualification;",
                    "a particular examination or assessment result;",
                    "successful completion of an external qualification;",
                    "employment;",
                    "promotion;",
                    "increased earnings;",
                    "any other specific academic or professional outcome."
                ]
            },
            {
                "type": "paragraph",
                "text": "Results depend on factors outside our control, including your existing knowledge, independent work, performance and decisions made by third-party institutions, trainers, assessors or awarding bodies."
            },
            {
                "type": "heading",
                "text": "12. Third-party qualifications, organisations and trade marks"
            },
            {
                "type": "paragraph",
                "text": "Course materials may refer to third-party qualifications, examinations, organisations, products or trade marks, including CELTA, DELTA, IELTS, Cambridge English qualifications and OET. Such references are made for educational, descriptive or professional-development purposes. Unless expressly stated otherwise, they do not imply sponsorship, endorsement, partnership or affiliation with the relevant third-party organisation."
            },
            {
                "type": "heading",
                "text": "13. Third-party services"
            },
            {
                "type": "paragraph",
                "text": "The website may rely on third-party providers for functions including payment processing, hosting, authentication, email delivery and other technical infrastructure. Purchases are processed through Paddle and may also be subject to Paddle's applicable Buyer Terms."
            },
            {
                "type": "heading",
                "text": "14. Personal data"
            },
            {
                "type": "paragraph",
                "text": "Personal data is handled in accordance with our Privacy Policy."
            },
            {
                "type": "heading",
                "text": "15. Statutory consumer rights"
            },
            {
                "type": "paragraph",
                "text": "Nothing in these Terms & Conditions excludes, restricts or removes any consumer right that cannot legally be excluded."
            },
            {
                "type": "heading",
                "text": "16. Changes to these terms"
            },
            {
                "type": "paragraph",
                "text": "These Terms & Conditions may be updated where reasonably necessary, including to reflect changes to:"
            },
            {
                "type": "list",
                "items": [
                    "the website or platform;",
                    "course delivery;",
                    "technical infrastructure;",
                    "third-party providers; or",
                    "applicable law."
                ]
            },
            {
                "type": "paragraph",
                "text": "The Terms applicable to a purchase will normally be the version in force when that purchase was made. Where a later change materially affects an existing user's ongoing rights or access, appropriate notice will be provided where required."
            },
            {
                "type": "heading",
                "text": "17. Governing law"
            },
            {
                "type": "paragraph",
                "text": "These Terms & Conditions are governed by the laws of England and Wales. If you are a consumer living elsewhere, you may also benefit from mandatory consumer protections available under the law of your country of residence. Nothing in these Terms is intended to remove rights that cannot legally be excluded."
            },
            {
                "type": "heading",
                "text": "18. Contact"
            },
            {
                "type": "paragraph",
                "text": "If you have any questions about these Terms & Conditions, your account or course access, please contact:"
            },
            {
                "type": "paragraph",
                "text": "Olha Popivnenko"
            },
            {
                "type": "paragraph",
                "text": "Email: olgapopivnenko35@gmail.com"
            },
            {
                "type": "paragraph",
                "text": "☐ I have read, understood and agree to the Terms & Conditions."
            }
        ]
    } as { title: string; blocks: WebsiteContentBlock[] },
    refunds: {
        "title": "Refund & cancellation policy",
        "blocks": [
            {
                "type": "paragraph",
                "text": "Last updated: 10 October 2026"
            },
            {
                "type": "paragraph",
                "text": "This Refund & Cancellation Policy applies to purchases of digital self-study courses created and provided by Olha Popivnenko."
            },
            {
                "type": "paragraph",
                "text": "Payments are processed by Paddle, which acts as the Merchant of Record for transactions made through this website. Paddle processes payments, applicable transaction taxes, and approved refunds relating to purchases."
            },
            {
                "type": "heading",
                "text": "1. Immediate access to digital content"
            },
            {
                "type": "paragraph",
                "text": "All courses sold through this website are digital products delivered online. Access to a purchased course begins immediately after successful payment and the course becomes available through your account."
            },
            {
                "type": "paragraph",
                "text": "Before completing your purchase, you will be asked to expressly confirm that:"
            },
            {
                "type": "list",
                "items": [
                    "you want the digital content to be made available immediately after payment; and",
                    "you understand that, once supply of the digital content begins, you lose the statutory 14-day right to cancel that would otherwise apply."
                ]
            },
            {
                "type": "paragraph",
                "text": "Once access to the digital content has begun following your express consent and acknowledgement, your statutory 14-day right to cancel the purchase will no longer apply. This does not affect any other statutory consumer rights you may have."
            },
            {
                "type": "heading",
                "text": "2. Change-of-mind refunds"
            },
            {
                "type": "paragraph",
                "text": "Once access to the purchased course has begun following your express consent and acknowledgement, refunds are not normally provided simply because you:"
            },
            {
                "type": "list",
                "items": [
                    "change your mind;",
                    "decide that you no longer want or need the course;",
                    "decide not to complete the course;",
                    "do not use the course;",
                    "do not complete the course within the access period; or",
                    "purchase a course and later decide that another course would have been more suitable."
                ]
            },
            {
                "type": "paragraph",
                "text": "Please read the course description carefully before completing your purchase to ensure that the course is suitable for your needs. This section does not affect any statutory rights that cannot legally be excluded."
            },
            {
                "type": "heading",
                "text": "3. Statutory consumer rights"
            },
            {
                "type": "paragraph",
                "text": "Nothing in this Refund & Cancellation Policy limits or removes any mandatory consumer rights available to you under applicable law. If digital content is faulty, materially different from its description, not supplied as agreed, or otherwise fails to conform to the contract, you may be entitled to an appropriate legal remedy. Depending on the circumstances and applicable law, this may include repair, replacement, a price reduction, or a refund. If you believe that a purchased course does not meet the standards required by law, please contact us so that the issue can be investigated."
            },
            {
                "type": "heading",
                "text": "4. Technical problems"
            },
            {
                "type": "paragraph",
                "text": "A temporary technical problem does not automatically create a right to a refund where the problem can reasonably be corrected. If you are unable to access a purchased course because of a technical problem with the Triad Teacher Training platform, please contact us so that we can investigate the issue and restore access where possible. If a technical problem for which we are responsible cannot reasonably be resolved, any refund or other remedy required by applicable consumer law will remain available. Problems caused by your own device, internet connection, browser, software, or other circumstances outside our reasonable control do not normally create a right to a refund."
            },
            {
                "type": "heading",
                "text": "5. Duplicate or incorrect charges"
            },
            {
                "type": "paragraph",
                "text": "If you believe that:"
            },
            {
                "type": "list",
                "items": [
                    "you have been charged more than once for the same purchase;",
                    "an incorrect amount has been charged;",
                    "another payment error has occurred,"
                ]
            },
            {
                "type": "paragraph",
                "text": "please contact us as soon as possible. We will investigate the issue and, where an incorrect charge is confirmed, arrange for the appropriate refund or correction to be processed through Paddle."
            },
            {
                "type": "heading",
                "text": "6. How refunds are processed"
            },
            {
                "type": "paragraph",
                "text": "Because Paddle acts as the Merchant of Record, refunds relating to purchases made through this website are processed through Paddle. Where Olha Popivnenko determines that a refund should be issued, the refund will be arranged through Paddle rather than sent separately by Triad Teacher Training. Where a full refund is issued, access to the relevant course will be withdrawn. Where appropriate, you may also contact Paddle directly regarding payment or refund-related issues."
            },
            {
                "type": "heading",
                "text": "7. Fraud, abuse and chargebacks"
            },
            {
                "type": "paragraph",
                "text": "Refund requests may be refused where there is reasonable evidence of fraud, refund abuse, account sharing, misuse of the course platform, or other manipulative behaviour, subject to applicable law. Attempting to obtain a refund or chargeback while continuing to access or use the relevant course may result in temporary suspension of course access while the matter is investigated. Nothing in this section limits any statutory rights or legitimate rights to dispute an unauthorised or incorrect transaction."
            },
            {
                "type": "heading",
                "text": "8. Requesting a refund or reporting a problem"
            },
            {
                "type": "paragraph",
                "text": "If you believe that you are legally entitled to a refund, have been charged incorrectly, or experience a problem with a purchased course, please contact:"
            },
            {
                "type": "paragraph",
                "text": "Olha Popivnenko"
            },
            {
                "type": "paragraph",
                "text": "Email: olgapopivnenko35@gmail.com"
            },
            {
                "type": "paragraph",
                "text": "Please include:"
            },
            {
                "type": "list",
                "items": [
                    "the email address associated with your account;",
                    "the name of the course purchased;",
                    "the approximate date of purchase; and",
                    "a brief explanation of the issue."
                ]
            },
            {
                "type": "paragraph",
                "text": "We may request additional information where reasonably necessary to investigate the request."
            },
            {
                "type": "heading",
                "text": "9. Consumer rights in other countries"
            },
            {
                "type": "paragraph",
                "text": "Customers may have additional mandatory consumer rights depending on their country of residence. Nothing in this Refund & Cancellation Policy is intended to exclude or restrict any consumer right that cannot legally be excluded under applicable law. Where local consumer-protection law provides you with additional mandatory cancellation, refund, or other rights, those rights will continue to apply."
            },
            {
                "type": "paragraph",
                "text": "☐ I expressly consent to receiving immediate access to the digital content after payment and acknowledge that, once supply begins, I will lose my statutory 14-day right to cancel."
            },
            {
                "type": "paragraph",
                "text": "☐ I have read and agree to the Refund & Cancellation Policy."
            }
        ]
    } as { title: string; blocks: WebsiteContentBlock[] },
};
